<template>
    <v-dialog :value="value" max-width="900" persistent @input="onDialogInput">
        <v-card :loading="isBusy">
            <v-card-title>
                <span class="headline">{{ $t('shopping_cart.new_recipient') }}</span>
                <v-spacer/>
                <v-btn icon :disabled="isBusy" :aria-label="$t('button.close')" @click="onClose">
                    <v-icon>mdi-window-close</v-icon>
                </v-btn>
            </v-card-title>
            <v-form ref="form" @submit.prevent="onSave">
                <v-card-text>
                    <v-alert v-if="creationState === 'saved'" type="info" text>
                        {{ $t('recipient_dialog.saved_readback_pending') }}
                    </v-alert>
                    <v-alert v-if="creationState === 'unknown'" type="warning" text>
                        {{ $t('recipient_dialog.creation_outcome_unknown') }}
                    </v-alert>
                    <v-alert v-if="errorMessage" type="error" text role="alert">
                        {{ errorMessage }}
                    </v-alert>
                    <v-radio-group v-model="mode" :disabled="formDisabled" row>
                        <v-radio value="new" :label="$t('recipient_dialog.new_buyer')"/>
                        <v-radio value="existing" :label="$t('recipient_dialog.existing_buyer')"/>
                    </v-radio-group>
                    <template v-if="mode === 'new'">
                        <h3 class="mb-4">{{ $t('buyers.personal_info') }}</h3>
                        <v-row>
                            <v-col v-for="field in buyerFields" :key="field.name" cols="12" md="4">
                                <v-text-field
                                    v-model="buyer[field.name]"
                                    :label="field.label"
                                    :rules="buyerRules(field)"
                                    :maxlength="field.maxLength"
                                    :required="field.name === 'name'"
                                    :class="{required: field.name === 'name'}"
                                    :type="field.type || 'text'"
                                    :disabled="formDisabled"
                                    outlined
                                    dense
                                    hide-details="auto"
                                />
                            </v-col>
                        </v-row>
                        <h3 class="mt-6 mb-4">{{ $t('buyers.delivery_address') }}</h3>
                        <recipient-address-fields v-model="deliveryAddress" :disabled="formDisabled"/>
                        <v-switch
                            v-model="sameBillingAddress"
                            :label="$t('recipient_dialog.same_billing_address')"
                            :disabled="formDisabled"
                            class="mt-0 mb-4"
                        />
                        <template v-if="!sameBillingAddress">
                            <h3 class="mb-4">{{ $t('buyers.billing_address') }}</h3>
                            <recipient-address-fields v-model="billingAddress" :disabled="formDisabled"/>
                        </template>
                    </template>
                    <template v-else>
                        <v-autocomplete
                            v-model="selectedBuyer"
                            :items="buyers"
                            :search-input.sync="buyerSearch"
                            :loading="isBusyBuyers"
                            :label="$t('recipient_dialog.buyer')"
                            :hint="$t('recipient_dialog.buyer_search_hint')"
                            :item-text="buyerLabel"
                            item-value="external_id"
                            :rules="[buyerRequired]"
                            :disabled="formDisabled"
                            :no-data-text="$t('recipient_dialog.no_buyers')"
                            return-object
                            no-filter
                            clearable
                            persistent-hint
                            outlined
                            dense
                        />
                        <h3 class="mt-6 mb-4">{{ $t('buyers.delivery_address') }}</h3>
                        <recipient-address-fields v-model="deliveryAddress" :disabled="formDisabled"/>
                        <v-switch
                            v-model="defaultAddress"
                            :label="$t('recipient_dialog.default_address')"
                            :disabled="formDisabled"
                            class="mt-0"
                        />
                    </template>
                </v-card-text>
                <v-card-actions class="flex-wrap">
                    <v-spacer/>
                    <v-btn text :disabled="isBusy" @click="onClose">{{ $t('button.close') }}</v-btn>
                    <v-btn
                        v-if="creationState === 'saved'"
                        color="primary"
                        :disabled="isBusy"
                        :loading="isBusy"
                        @click="retryReadback"
                    >
                        {{ $t('recipient_dialog.retry_readback') }}
                    </v-btn>
                    <v-btn
                        v-else-if="creationState === 'editable'"
                        type="submit"
                        color="primary"
                        :disabled="isBusy"
                        :loading="isBusy"
                    >
                        {{ $t('recipient_dialog.save_and_add') }}
                    </v-btn>
                </v-card-actions>
            </v-form>
        </v-card>
    </v-dialog>
</template>

<script>
import _ from 'lodash';
import {email} from 'vuelidate/lib/validators';
import {API} from '@/api/api';
import RecipientAddressFields from './RecipientAddressFields';

const emptyAddress = () => ({
    street: null,
    house_number: null,
    suburb: null,
    city: null,
    postal_code: null,
    country: null,
    country_iso_code: null,
    phone: null,
    email: null
});

const emptyBuyer = () => ({name: null, ico: null, dic: null, phone: null, email: null, website: null});

const cleanFields = fields => Object.keys(fields).reduce((result, name) => {
    const value = typeof fields[name] === 'string' ? fields[name].trim() : fields[name];
    result[name] = value === '' ? null : value;
    return result;
}, {});

export default {
    name: 'CreateRecipientDialog',
    components: {RecipientAddressFields},
    props: {
        value: {type: Boolean, required: true},
        warehouseId: {type: Number, required: true}
    },
    data: () => ({
        mode: 'new',
        buyer: emptyBuyer(),
        billingAddress: emptyAddress(),
        deliveryAddress: emptyAddress(),
        sameBillingAddress: true,
        defaultAddress: false,
        selectedBuyer: null,
        buyers: [],
        buyerSearch: null,
        buyerRequestCounter: 0,
        isBusyBuyers: false,
        isBusy: false,
        creationState: 'editable',
        savedCreation: null,
        errorMessage: null,
        formSession: 0,
        debouncedBuyerSearch: null
    }),
    computed: {
        formDisabled() {
            return this.isBusy || this.creationState !== 'editable';
        },
        buyerFields() {
            return [
                {name: 'name', maxLength: 100},
                {name: 'ico', maxLength: 30},
                {name: 'dic', maxLength: 30},
                {name: 'phone', maxLength: 20, type: 'tel'},
                {name: 'email', maxLength: 50, type: 'email'},
                {name: 'website'}
            ].map(field => ({...field, label: this.$t('buyers.' + field.name)}));
        }
    },
    watch: {
        value: {
            immediate: true,
            handler(open) {
                if (open) {
                    this.resetForm();
                } else {
                    this.buyerRequestCounter += 1;
                    if (this.debouncedBuyerSearch) this.debouncedBuyerSearch.cancel();
                }
            }
        },
        warehouseId() {
            this.buyerRequestCounter += 1;
            if (this.value && !this.isBusy) {
                this.$emit('input', false);
            }
        },
        mode() {
            this.errorMessage = null;
            this.$nextTick(() => this.$refs.form && this.$refs.form.resetValidation());
            if (this.mode === 'existing') {
                this.fetchBuyers(this.buyerSearch);
            } else {
                this.buyerRequestCounter += 1;
                this.isBusyBuyers = false;
            }
        },
        buyerSearch(filter) {
            if (this.debouncedBuyerSearch) this.debouncedBuyerSearch(filter);
        }
    },
    created() {
        this.debouncedBuyerSearch = _.debounce(filter => this.fetchBuyers(filter), 250);
    },
    beforeDestroy() {
        this.formSession += 1;
        this.buyerRequestCounter += 1;
        this.debouncedBuyerSearch.cancel();
    },
    methods: {
        buyerLabel(buyer) {
            return ['#' + buyer.external_id, buyer.name, buyer.ico].filter(Boolean).join(', ');
        },
        buyerRequired(value) {
            return Boolean(value && value.external_id) || this.$t('recipient_dialog.buyer_required');
        },
        buyerRules(field) {
            const rules = [];
            if (field.maxLength) {
                rules.push(value => !value || String(value).length <= field.maxLength
                    || this.$t('recipient_dialog.max_length', {max: field.maxLength}));
            }
            if (field.name === 'name') {
                rules.push(value => Boolean(value && String(value).trim()) || this.$t('validation.name.required'));
            }
            if (field.name === 'email') {
                rules.push(value => email(value) || this.$t('validation.email.email'));
            }
            return rules;
        },
        async fetchBuyers(filter) {
            if (!this.value || this.mode !== 'existing' || this.formDisabled) {
                return;
            }
            const requestNumber = ++this.buyerRequestCounter;
            this.isBusyBuyers = true;
            try {
                const response = await API.get('/api/buyers/by-warehouse/' + this.warehouseId, {
                    params: {page: 1, perPage: 100, filter: filter || ''},
                    preserveError: true
                });
                if (requestNumber === this.buyerRequestCounter) {
                    this.buyers = response.data.items;
                    if (this.selectedBuyer && !this.buyers.some(buyer => buyer.external_id === this.selectedBuyer.external_id)) {
                        this.buyers.unshift(this.selectedBuyer);
                    }
                }
            } catch (error) {
                if (requestNumber === this.buyerRequestCounter) {
                    this.errorMessage = API.errorMessage(error);
                }
            } finally {
                if (requestNumber === this.buyerRequestCounter) {
                    this.isBusyBuyers = false;
                }
            }
        },
        async onSave() {
            if (this.formDisabled || !this.$refs.form.validate()) {
                return;
            }
            const warehouseId = this.warehouseId;
            const formSession = this.formSession;
            const mode = this.mode;
            const deliveryAddress = cleanFields(this.deliveryAddress);
            const payload = mode === 'new'
                ? {
                    ...cleanFields(this.buyer),
                    warehouse: warehouseId,
                    billing_address: cleanFields(this.sameBillingAddress ? this.deliveryAddress : this.billingAddress),
                    delivery_address: deliveryAddress
                }
                : {warehouse: warehouseId, default: this.defaultAddress, address: deliveryAddress};
            const url = mode === 'new' ? '/api/buyers'
                : '/api/buyers/' + encodeURIComponent(this.selectedBuyer.external_id) + '/addresses';
            this.isBusy = true;
            this.errorMessage = null;
            this.buyerRequestCounter += 1;
            this.isBusyBuyers = false;
            try {
                const response = await API.post(url, payload, {preserveError: true});
                if (formSession !== this.formSession) return;
                const result = response.data;
                if (!result || !result.external_buyer_id) {
                    this.creationState = 'unknown';
                    return;
                }
                this.creationState = 'saved';
                this.savedCreation = {...result, mode, warehouseId};
                if (result.recipient) {
                    this.finishCreation(result.recipient, warehouseId);
                }
            } catch (error) {
                if (formSession !== this.formSession) return;
                if (!error.response || error.response.data?.creation_outcome_unknown) {
                    this.creationState = 'unknown';
                } else {
                    this.errorMessage = API.errorMessage(error);
                }
            } finally {
                if (formSession === this.formSession) {
                    this.isBusy = false;
                    if (warehouseId !== this.warehouseId) this.$emit('input', false);
                }
            }
        },
        async retryReadback() {
            if (this.isBusy || this.creationState !== 'saved' || !this.savedCreation) return;
            const saved = this.savedCreation;
            const formSession = this.formSession;
            this.isBusy = true;
            this.errorMessage = null;
            try {
                const [buyerResponse, addressResponse] = await Promise.all([
                    API.get('/api/buyers/' + saved.external_buyer_id, {preserveError: true}),
                    API.get('/api/addresses/by-buyer/' + saved.external_buyer_id, {preserveError: true})
                ]);
                if (formSession !== this.formSession) return;
                const buyer = buyerResponse.data;
                const deliveryAddress = addressResponse.data.find(item => saved.mode === 'new'
                    ? item.default === true
                    : String(item.address.external_id) === String(saved.address_external_id));
                if (!deliveryAddress || !deliveryAddress.address.external_id
                    || String(buyer.external_id) !== String(saved.external_buyer_id)) {
                    this.errorMessage = this.$t('recipient_dialog.readback_unavailable');
                    return;
                }
                const address = deliveryAddress.address;
                this.finishCreation({
                    ...address,
                    address_external_id: address.external_id,
                    external_buyer_id: buyer.external_id,
                    name: buyer.name,
                    ico: buyer.ico,
                    dic: buyer.dic,
                    phone: address.phone ?? buyer.phone,
                    email: address.email ?? buyer.email
                }, saved.warehouseId);
            } catch (error) {
                if (formSession === this.formSession) this.errorMessage = API.errorMessage(error);
            } finally {
                if (formSession === this.formSession) {
                    this.isBusy = false;
                    if (saved.warehouseId !== this.warehouseId) this.$emit('input', false);
                }
            }
        },
        finishCreation(recipient, warehouseId) {
            if (!this.value || warehouseId !== this.warehouseId) return;
            if (!recipient.address_external_id || !recipient.external_buyer_id
                || String(recipient.external_buyer_id) !== String(this.savedCreation.external_buyer_id)) {
                this.errorMessage = this.$t('recipient_dialog.readback_unavailable');
                return;
            }
            this.$emit('created', recipient);
            this.$emit('input', false);
        },
        onDialogInput(open) {
            if (!open) this.onClose();
        },
        onClose() {
            if (!this.isBusy) this.$emit('input', false);
        },
        resetForm() {
            this.formSession += 1;
            this.buyerRequestCounter += 1;
            this.mode = 'new';
            this.buyer = emptyBuyer();
            this.billingAddress = emptyAddress();
            this.deliveryAddress = emptyAddress();
            this.sameBillingAddress = true;
            this.defaultAddress = false;
            this.selectedBuyer = null;
            this.buyers = [];
            this.buyerSearch = null;
            this.isBusyBuyers = false;
            this.isBusy = false;
            this.creationState = 'editable';
            this.savedCreation = null;
            this.errorMessage = null;
            this.$nextTick(() => this.$refs.form && this.$refs.form.resetValidation());
        }
    }
};
</script>
